import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, ArrowRight, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import stripeService from '../../services/stripeService';
import { orgService } from '../../services/orgService';
import { getCurrentUser } from 'aws-amplify/auth';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [supervisorSeats, setSupervisorSeats] = useState(1);
  const [supervisorBusy, setSupervisorBusy] = useState(false);

  useEffect(() => { loadUserData(); }, []);

  const loadUserData = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      const subStatus = await stripeService.getSubscriptionStatus(user.userId);
      setSubscription(subStatus);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const startSupervisorCheckout = async () => {
    setSupervisorBusy(true);
    try {
      const { url } = await orgService.startCheckout(supervisorSeats);
      if (url) { window.location.href = url; return; }
      toast.error('Could not start checkout. Please try again.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start checkout.');
    } finally {
      setSupervisorBusy(false);
    }
  };

  const plans = stripeService.getSubscriptionPlans();
  const free = plans.individual_monthly;
  const sup = plans.practice;

  const Badge = ({ color, icon: Icon, label }) => (
    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
      <div className={`${color} text-white px-4 py-1 rounded-full text-sm font-bold flex items-center shadow`}>
        <Icon className="w-4 h-4 mr-1" /> {label}
      </div>
    </div>
  );

  const FeatureList = ({ features }) => (
    <ul className="space-y-3 mb-6">
      {features.map((f, i) => (
        <li key={i} className="flex items-start">
          <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700 text-base">{f}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Plans &amp; Pricing</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto font-medium">
            A complete <span className="font-bold text-gray-900">AI documentation infrastructure and visual-analytics monitoring system</span> for your practice — free for individual clinicians, or <span className="font-bold text-gray-900">$15 per seat</span> to equip and monitor your entire team.
          </p>
        </div>

        {/* Three boxes side by side: Free · Supervisor · How it works */}
        <div className="grid lg:grid-cols-3 gap-6 items-start mb-10">

          {/* Free plan */}
          <div className="relative bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
            {subscription?.plan === 'individual_monthly' && (
              <Badge color="bg-green-500" icon={Check} label="Current Plan" />
            )}
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{free.name}</h3>
            <div className="text-4xl font-bold text-gray-900 mb-6">$0</div>
            <FeatureList features={free.features} />
            <div className="w-full py-3 px-6 rounded-lg font-semibold text-center bg-gray-100 text-gray-500 text-base">
              Included with every account
            </div>
          </div>

          {/* Supervisor plan — Most Popular, compact so the CTA is visible without scrolling */}
          <div className="relative bg-white rounded-2xl shadow-xl border-2 border-purple-500 p-8 lg:scale-105">
            <Badge color="bg-purple-600" icon={Star} label="Most Popular" />
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{sup.name}</h3>
            <div className="text-4xl font-bold text-gray-900 mb-1">$15<span className="text-lg font-semibold text-gray-500"> / seat / month</span></div>
            <p className="text-sm text-gray-500 mb-6">Equip and monitor your whole practice.</p>

            <div className="flex items-center justify-between bg-purple-50 rounded-lg px-4 py-3 mb-3">
              <div>
                <div className="text-base font-bold text-gray-800">Total seats</div>
                <div className="text-sm text-gray-500">You (1) + your clinicians</div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setSupervisorSeats(s => Math.max(1, s - 1))}
                  className="w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold text-lg">−</button>
                <span className="w-8 text-center font-bold text-gray-900 text-lg">{supervisorSeats}</span>
                <button type="button" onClick={() => setSupervisorSeats(s => Math.min(200, s + 1))}
                  className="w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold text-lg">+</button>
              </div>
            </div>
            <div className="text-center mb-3">
              <span className="text-3xl font-bold text-gray-900">${supervisorSeats * 15}</span>
              <span className="text-gray-500"> / month · {supervisorSeats} {supervisorSeats === 1 ? 'seat' : 'seats'} × $15</span>
            </div>
            <button
              onClick={startSupervisorCheckout}
              disabled={supervisorBusy}
              className="w-full py-3 px-6 rounded-lg font-bold text-lg transition-all duration-200 flex items-center justify-center bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white shadow"
            >
              {supervisorBusy ? 'Starting checkout…' : `Subscribe — $${supervisorSeats * 15}/mo`}
              {!supervisorBusy && <ArrowRight className="w-5 h-5 ml-2" />}
            </button>
            <p className="text-sm text-gray-400 text-center mt-2">Secure checkout · cancel anytime · start with just your own seat.</p>

            <div className="mt-6">
              <FeatureList features={sup.features} />
            </div>
          </div>

          {/* How the Supervisor Plan works — its own box, alongside the plans */}
          <div className="bg-purple-50 border-2 border-purple-100 rounded-2xl p-8">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="w-6 h-6 text-purple-700" />
              <h3 className="text-xl font-bold text-purple-900">How the Supervisor Plan works</h3>
            </div>
            <p className="text-base text-purple-900 font-semibold mb-4">
              Provide a whole AI documentation and visual-analytics infrastructure for your entire practice — one account for every employee, and one dashboard to monitor them all.
            </p>
            <ol className="text-base text-purple-900/90 space-y-3 list-decimal list-inside">
              <li><span className="font-bold">You are seat #1</span> — a full Chartara Notes account plus the supervisor dashboard.</li>
              <li><span className="font-bold">Every seat is $15/month, including yours</span> — one per person: you + each clinician.</li>
              <li><span className="font-bold">Invite clinicians by email.</span> They sign in free, and their visual analytics roll up to you.</li>
              <li><span className="font-bold">Add or remove seats anytime</span> — extra seats are prorated for the current month.</li>
            </ol>
          </div>
        </div>

        {/* Manage current plan */}
        <div className="text-center">
          <p className="text-base text-gray-600 mb-3 font-medium">Already subscribed? Update your card, seats, or cancel anytime.</p>
          <button
            onClick={() => window.open('https://billing.stripe.com/p/login/4gM3cw7z06TQcpNaNk4Rq00', '_blank')}
            className="inline-flex items-center px-6 py-3 border-2 border-gray-300 rounded-lg text-base font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
          >
            Manage Current Plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
